<template lang="pug">
.v-field-map.field-map
	.field-check-list__label(v-if="field.label" :id="uniqueId")
		| {{field.label}}
		span.field-text__star(v-if="field.required") *
	.field-map__instruction.flc(v-if="field.instruction || field.confirm") {{ field.instruction || field.confirm }}
	.field-map__controls(v-if="field.allowRadius")
		.field-map__mode(role="group")
			button.field-map__control-button(
				v-for="option in field.modeOptions"
				:key="option.value"
				type="button"
				:class="{'field-map__control-button--active': mode === option.value}"
				:aria-pressed="mode === option.value"
				@click="setMode(option.value)") {{ option.text }}
		.field-map__radius(v-if="mode === 'radius'")
			button.field-map__radius-button(
				v-for="option in field.radiusOptions"
				:key="option.value"
				type="button"
				:class="{'field-map__radius-button--active': radius === Number(option.value)}"
				:aria-pressed="radius === Number(option.value)"
				@click="setRadius(option.value)") {{ option.text }}
	.field-map__canvas
		.field-map__map(ref="map")
		button.field-map__zoom-button(type="button" @click="openModal")
			v-svg-icon.field-map__zoom-button--icon(icon="open-full")
</template>

<script>
import { Loader } from "@googlemaps/js-api-loader"
import uniqueId from '../scripts/helpers/uniqueId.js';
import controller from "../scripts/controller/controller.js";

const POINT_MODE = 'point';
const RADIUS_MODE = 'radius';
const DEFAULT_RADIUS_KM = 0.5;
const CIRCLE_STYLE = {
	strokeColor: '#42948E',
	strokeOpacity: 0.95,
	strokeWeight: 2,
	fillColor: '#42948E',
	fillOpacity: 0.24,
};

export default {
	props: {
		field: {
			type: Object,
			required: true,
			default: null
		},

		modelValue: {
			type: [Object, String, Number, Boolean],
			default: null
		}
	},

	emits: [
		'update:model-value'
	],

	data() {
		return {
			localValue: null,
			position: null,
			zoom: null,
			mode: POINT_MODE,
			radius: DEFAULT_RADIUS_KM,
			touched: false,
			uniqueId: uniqueId('field-'),
			mapSyncScheduled: false,
			mapRenderToken: 0
		};
	},

	watch: {
		modelValue: {
			deep: true,
			handler() {
				this.syncFromProps();
			}
		},

		field() {
			this.syncFromProps();
		}
	},

	created() {
		this.updateLocalValue();
		this.setInitValue();
	},

	mounted() {
		// Даже если пользователь не двигает уже установленную по умолчанию метку,
		// её текущий центр должен попасть в форму и сохраниться без чекбокса.
		this.emitCurrentValue();
		this.scheduleMapSync();
	},

	beforeUnmount() {
		this.mapRenderToken++;
	},

	methods: {
		toBoolean(value) {
			return value === true || value === 1 || value === '1' || value === 'true';
		},

		hasPosition(value) {
			if (!value || value.lat === '' || value.lat === null || value.lat === undefined
				|| value.lng === '' || value.lng === null || value.lng === undefined) {
				return false;
			}

			return Number.isFinite(Number(value.lat)) && Number.isFinite(Number(value.lng));
		},

		normalizeRadius(value) {
			const radius = Number(String(value ?? '').replace(',', '.'));
			const allowed = (this.field?.radiusOptions || []).map(option => Number(option.value));
			return allowed.includes(radius) ? radius : DEFAULT_RADIUS_KM;
		},

		normalizeMode(value) {
			return this.field?.allowRadius && value === RADIUS_MODE ? RADIUS_MODE : POINT_MODE;
		},

		syncFromProps() {
			this.updateLocalValue();
			this.setInitValue();
			this.emitCurrentValue();
			this.scheduleMapSync();
		},

		scheduleMapSync() {
			if (!this.$el || this.mapSyncScheduled) {
				return;
			}

			this.mapSyncScheduled = true;
			this.$nextTick(() => {
				this.mapSyncScheduled = false;
				this.initMap();
			});
		},

		async initMap() {
			const mapElement = this.$refs.map;
			if (!mapElement || !this.position) {
				return;
			}

			const renderToken = ++this.mapRenderToken;
			const loader = new Loader({
				apiKey: "AIzaSyBdN0KVu692vSBW1wd1QHP0TUSw-oPfcu4",
				version: "weekly",
				language: this.field?.language || "en"
			});

			await loader.load();
			const [{ Map }, { AdvancedMarkerElement }] = await Promise.all([
				google.maps.importLibrary("maps"),
				google.maps.importLibrary("marker")
			]);

			if (renderToken !== this.mapRenderToken || this.$refs.map !== mapElement) {
				return;
			}

			const map = new Map(mapElement, {
				center: this.position,
				zoom: this.zoom,
				streetViewControl: false,
				mapTypeControl: false,
				fullscreenControl: false,
				clickableIcons: false,
				mapId: '952e5846ea5cd60b'
			});

			if (this.field?.bounds && !this.touched) {
				map.fitBounds({
					"south": Number(this.field.bounds.southwest.lat),
					"west": Number(this.field.bounds.southwest.lng),
					"north": Number(this.field.bounds.northeast.lat),
					"east": Number(this.field.bounds.northeast.lng)
				});
			}

			if (this.mode === RADIUS_MODE) {
				const circle = new google.maps.Circle({
					...CIRCLE_STYLE,
					map,
					center: this.position,
					radius: this.radius * 1000,
					draggable: true,
					editable: false,
				});
				circle.addListener('dragend', () => {
					const center = circle.getCenter();
					this.setPosition(center.lat(), center.lng(), map.zoom);
				});
				return;
			}

			const markerImg = document.createElement("img");
			markerImg.src = this.field?.src;

			const advancedMarker = new AdvancedMarkerElement({
				map,
				position: this.position,
				content: markerImg,
				gmpDraggable: true,
			});

			advancedMarker.addListener('dragend', () => {
				const markerPosition = advancedMarker.position;
				this.setPosition(markerPosition.lat, markerPosition.lng, map.zoom);
			});
		},

		openModal() {
			controller.openModal({
				className: 'map-zoomed',
				component: {
					name: "v-modal-map",
					props: {
						dataSource: {
							zoom: this.zoom,
							position: this.position,
							marker: this.field?.src,
							mode: this.mode,
							radius: this.radius,
							language: this.field?.language || 'en',
							readonly: false
						}
					},
					listeners: {
						'set-position': (data) => {
							this.position = {lat: Number(data.lat), lng: Number(data.lng)};
							this.zoom = Number(data.zoom) || this.zoom;
							this.mode = this.normalizeMode(data.mode);
							this.radius = this.normalizeRadius(data.radius);
							this.touched = true;
							this.emitCurrentValue();
							this.scheduleMapSync();
						}
					}
				},
			});
		},

		updateLocalValue() {
			this.localValue = this.modelValue && typeof this.modelValue === 'object'
				? {...this.modelValue}
				: {};
		},

		setInitValue() {
			const value = this.localValue || {};
			const touched = this.toBoolean(value.touched);
			const fieldCenter = this.hasPosition(this.field?.center) ? this.field.center : null;
			const valuePosition = this.hasPosition(value) ? value : null;
			const initialPosition = valuePosition && (touched || !fieldCenter) ? valuePosition : fieldCenter;

			this.position = initialPosition
				? {lat: Number(initialPosition.lat), lng: Number(initialPosition.lng)}
				: {lat: 35, lng: 33.3};
			this.zoom = Number(value.zoom || this.field?.center?.zoom || 8);
			this.mode = this.normalizeMode(value.mode);
			this.radius = this.normalizeRadius(value.radius);
			this.touched = touched;
		},

		setMode(mode) {
			this.mode = this.normalizeMode(mode);
			this.emitCurrentValue();
			this.scheduleMapSync();
		},

		setRadius(radius) {
			this.radius = this.normalizeRadius(radius);
			this.emitCurrentValue();
			this.scheduleMapSync();
		},

		setPosition(lat, lng, zoom) {
			this.position = {lat: Number(lat), lng: Number(lng)};
			this.zoom = Number(zoom) || this.zoom;
			this.touched = true;
			this.emitCurrentValue();
		},

		emitCurrentValue() {
			if (!this.position) return;
			const value = {
				lat: this.position.lat,
				lng: this.position.lng,
				zoom: this.zoom,
				mode: this.mode,
				radius: this.radius,
				touched: this.touched
			};
			if (JSON.stringify(value) === JSON.stringify(this.localValue)) return;
			this.localValue = value;
			this.$emit('update:model-value', value);
		}
	}
};
</script>
